import { pipe } from "effect"
import { WithQueryClient, WithRuntime } from "@open-revenue/effect-react-query"
import ProgressBar from "@badrap/bar-of-progress"
import { AppProps, ErrorFallbackProps, ErrorBoundary as _ErroBountry } from "@blitzjs/next"
import { useQueryErrorResetBoundary } from "@blitzjs/rpc"
import LoginForm from "app/auth/components/LoginForm"
import { withBlitz } from "app/blitz-client"
import { BrowserContextProvider } from "app/core/contexts/BrowserContext"
import { useIdentifyUser } from "app/core/hooks/useAnalytics"
import "app/core/nativeTypeExtensions"
import "app/core/styles/index.css"
import { AppRuntime } from "app/core/runtime/AppRuntime"
import { AuthenticationError, AuthorizationError } from "blitz"
import LogRocket from "logrocket"
import { Router, useRouter } from "next/router"
import Script from "next/script"
import ErrorComponent from "pages/_error"
import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo } from "react"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { IntercomProvider } from "react-use-intercom"

const ErrorBoundary = _ErroBountry as any

// Import the StaffBar client
import StaffBar from "@staffbar/client"
import StaffBarProvider from "app/core/components/misc/StaffBarProvider"
import { trpc } from "app/core/trpcClient"
import DocController from "app/docs/components/DocController"
import { june } from "app/misc/june"
import initMixpanel from "app/misc/mixpanel"
import { Toaster } from "@superwall/ui"

// Setup the StaffBar client
export let staffbar: StaffBar
// Prevents this from running on the server assuming you're using Next.js
if (typeof window != "undefined") {
  staffbar = new StaffBar(
    async () => {
      // const { token } = await invoke(adminGetStaffBarToken, {})
      // return token
      return null
    },
    { debug: true }
  )
}

// // HACK to get around prequel's CORS headers
// if (typeof window !== "undefined") {
//   // Hook fetch so we can intercept requests and redirect prequel requests to
//   // our api server.
//   const originalFetch = window.fetch
//   window.fetch = async function (input, init) {
//     // If the input url points to `api.prequel.co`, we'll redirect it to our api
//     // server.
//     if (typeof input === "string") {
//       try {
//         const windowURL = new URL(window.location.href)
//         const url = new URL(input, window.location.origin)
//         console.log(`url: ${url}`, url.origin)
//         if (url.origin === "https://api.prequel.co") {
//           url.host = windowURL.host
//           url.protocol = windowURL.protocol
//           url.pathname = "/api/prequel/" + url.pathname
//           return originalFetch(url.toString(), init)
//         }
//       } catch (e) {
//         return originalFetch(input, init)
//       }
//     }
//     return originalFetch(input, init)
//   }
// }

if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
  LogRocket.init("superwall-xjmfg/paywall-next")
}

const progress = new ProgressBar({
  size: 2,
  color: "#74FFF1",
  className: "bar-of-progress",
  delay: 100,
})

Router.events.on("routeChangeStart", progress.start)
Router.events.on("routeChangeComplete", progress.finish)
Router.events.on("routeChangeError", progress.finish)

type ProgressBarContextType = {
  start: () => void
  finish: () => void
}
export const ProgressBarContext = createContext<ProgressBarContextType>({
  start: () => {},
  finish: () => {},
})
export const ProgressBarProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <ProgressBarContext.Provider
      value={{
        start: progress.start,
        finish: progress.finish,
      }}
    >
      {children}
    </ProgressBarContext.Provider>
  )
}

export const useProgress = () => {
  return useContext(ProgressBarContext)
}

export const AnalyticsProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const router = useRouter()
  useIdentifyUser()
  useEffect(() => {
    // Track the initial page load
    if (june) june.page(router.asPath)

    const handleRouteChange = (url: string, { shallow }) => {
      if (june) june.page(url)
    }

    router.events.on("routeChangeStart", handleRouteChange)

    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events])

  // Initialize Mixpanel & track the page view on load
  useEffect(() => {
    const mixPanel = initMixpanel()
    const setup = async () => {
      const user = await june?.user()
      const userId = user.id()?.toString() || user.anonymousId()?.toString()
      console.log(`userId: ${userId}`)
      if (userId) mixPanel?.identify(userId)
      mixPanel?.track_pageview()
    }
    setup()

    const handleRouteChange = (url: string) => {
      mixPanel?.track_pageview({
        path: url,
      })
    }

    router.events.on("routeChangeComplete", handleRouteChange)

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange)
    }
  }, [router.events])

  const memoizedChildren = useMemo(() => <>{children}</>, [children])
  return memoizedChildren
}

const InnerApp = ({ Component: _Component, pageProps }: AppProps) => {
  const Component = _Component as any
  const getLayout = Component.getLayout || ((page) => page)

  return (
    <ErrorBoundary
      FallbackComponent={RootErrorFallback}
      onReset={useQueryErrorResetBoundary().reset}
    >
      <Script id="twitter">
        {`
            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
            },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
            a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
            twq('config','o5gil');
          `}
      </Script>
      <Script id="koala">
        {` !function(t){if(window.ko)return;window.ko=[],["identify","track","removeListeners","open","on","off","qualify","ready"].forEach(function(t){ko[t]=function(){var n=[].slice.call(arguments);return n.unshift(t),ko.push(n),ko}});var n=document.createElement("script");n.async=!0,n.setAttribute("src","https://cdn.getkoala.com/v1/pk_bb14368d62edc25720162725191d610bbd28/sdk.js"),(document.body || document.head).appendChild(n)}(); `}
      </Script>
      <Script src="https://www.googletagmanager.com/gtag/js?id=G-Z8XPHZ9NGB" />
      <Script id="google-analytics">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-Z8XPHZ9NGB');
        `}
      </Script>
      <Script id="pylon">
        {`
          (function(){var e=window;var t=document;var n=function(){n.e(arguments)};n.q=[];n.e=function(e){n.q.push(e)};e.Pylon=n;var r=function(){var e=t.createElement("script");e.setAttribute("type","text/javascript");e.setAttribute("async","true");e.setAttribute("src","https://widget.usepylon.com/widget/3016d5e1-846d-4920-a1c0-7093acfdef82");var n=t.getElementsByTagName("script")[0];n.parentNode.insertBefore(e,n)};if(t.readyState==="complete"){r()}else if(e.addEventListener){e.addEventListener("load",r,false)}})();
        `}
      </Script>

      <BrowserContextProvider>
        <IntercomProvider appId={"oyf4e3ep"} autoBoot autoBootProps={{ hideDefaultLauncher: true }}>
          <AnalyticsProvider>
            <ProgressBarProvider>{getLayout(<Component {...pageProps} />)}</ProgressBarProvider>
            <DocController />
          </AnalyticsProvider>
        </IntercomProvider>
      </BrowserContextProvider>
      <StaffBarProvider />

      <Toaster type="dashboard" />

      {/* The `position` prop for the Toast component doesn't seem to work */}
      {/* Resorting to overriding the CSS via `style` prop */}
      <ToastContainer autoClose={5000} style={{ top: "auto", bottom: "-10px" }} />
    </ErrorBoundary>
  )
}

export default pipe(
  trpc.withTRPC(
    withBlitz(function App(props: AppProps) {
      // This should prevent the blitz-rerender from propogating
      const memoizedInnerApp = useMemo(() => <InnerApp {...props} />, [props])
      return memoizedInnerApp
    })
  ),
  WithRuntime(AppRuntime),
  WithQueryClient(queryClient)
)

function RootErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  if (error instanceof AuthenticationError) {
    return <LoginForm onSuccess={resetErrorBoundary} />
  } else if (error instanceof AuthorizationError) {
    return (
      <ErrorComponent
        statusCode={error.statusCode}
        title="Sorry, you are not authorized to access this"
      />
    )
  } else {
    return (
      <ErrorComponent statusCode={error.statusCode || 400} title={error.message || error.name} />
    )
  }
}
